import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../component/ui/Card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../../component/ui/Chart";
import {
    COLORS,
    CHART_TYPE_MAP,
    BarChartDataFormat,
    BarChartConfigType,
} from "../constants/chartConstants";

// Defines the order of the bar segments
const BAR_SEGMENT_ORDER = [
    { key: "wind", color: COLORS.yellow, label: "Wind" },
    { key: "thermal", color: COLORS.pink, label: "Thermal" },
    { key: "solar", color: COLORS.blue, label: "Solar" },
    { key: "battery", color: COLORS.green, label: "Battery/Storage" },
    { key: "other", color: COLORS.orange, label: "Other" },
];

export function QueueInsightsBarChart(chartConfig: BarChartConfigType) {
    const data = chartConfig.data;

    // Find the top segment for each bar (last in BAR_SEGMENT_ORDER)
    const topBarKey = BAR_SEGMENT_ORDER[BAR_SEGMENT_ORDER.length - 1].key;

    // Calculate the maximum value across all data points
    const getMaxValue = (data: BarChartDataFormat[]) => {
        return Math.max(
            ...data.map(
                (item) =>
                    item.solar +
                    item.wind +
                    item.battery +
                    item.thermal +
                    item.other
            )
        );
    };

    // add some padding above the max value
    const maxValue = getMaxValue(data);
    const roundValue =
        maxValue > 10000
            ? 1000
            : maxValue > 500
              ? 500
              : maxValue > 100
                ? 100
                : maxValue > 10
                  ? 10
                  : 5;
    const domainMax =
        Math.ceil((maxValue + maxValue / 10) / roundValue) * roundValue;

    return (
        <Card className="text-neutral-600 dark:text-[#CEE0EF] dark:bg-[#2D3748] bg-white w-full h-full shadow-none border-none">
            <CardHeader className="p-0 space-y-0 pt-[10px]">
                <CardTitle className="text-center text-lg font-semibold text-neutral-600 dark:text-[#CEE0EF]">
                    {chartConfig.title}
                </CardTitle>
            </CardHeader>
            <>
                {data.length > 0 ? (
                    <CardContent className="p-0 flex items-center h-[calc(100%-40px)]">
                        <ResponsiveContainer
                            height="100%"
                            className="min-w-[210px] min-h-[210px] flex items-center grow"
                        >
                            <ChartContainer config={CHART_TYPE_MAP}>
                                <BarChart
                                    data={data}
                                    className="pr-[20px] flex grow"
                                >
                                    <CartesianGrid
                                        strokeDasharray="0"
                                        stroke="#7A8690"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        axisLine={{
                                            stroke: "#7A8690",
                                            strokeWidth: 2,
                                        }}
                                        tickLine={false}
                                        tick={{
                                            fill: "currentColor",
                                            fontSize: 10,
                                            className:
                                                "fill-neutral-600 dark:fill-[#CEE0EF]",
                                        }}
                                        angle={-90}
                                        textAnchor="end"
                                        interval={0}
                                        dy={-5}
                                        height={70}
                                        padding={{ left: 10, right: 10 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{
                                            fill: "currentColor",
                                            fontSize: 10,
                                            className:
                                                "fill-neutral-600 dark:fill-[#CEE0EF]",
                                        }}
                                        tickFormatter={(v) =>
                                            v.toLocaleString()
                                        }
                                        domain={[0, domainMax]}
                                        tickCount={6}
                                        allowDecimals={false}
                                    />
                                    <ChartTooltip
                                        content={<ChartTooltipContent />}
                                    />
                                    {BAR_SEGMENT_ORDER.map((bar, idx) => (
                                        <Bar
                                            key={bar.key}
                                            dataKey={bar.key}
                                            stackId="a"
                                            fill={bar.color}
                                            radius={
                                                bar.key === topBarKey
                                                    ? [4, 4, 0, 0]
                                                    : [0, 0, 0, 0]
                                            }
                                        />
                                    ))}
                                </BarChart>
                            </ChartContainer>
                        </ResponsiveContainer>
                    </CardContent>
                ) : (
                    <>
                        {chartConfig.textOverride ? (
                            <CardContent className="flex items-center justify-center h-[calc(100%-40px)]">
                                {chartConfig.textOverride}
                            </CardContent>
                        ) : (
                            <CardContent className="flex items-center justify-center h-[calc(100%-40px)]">
                                No data available
                            </CardContent>
                        )}
                    </>
                )}
            </>
        </Card>
    );
}

export default QueueInsightsBarChart;
